import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Discount } from 'shared/types/cart';
import AccordionBtn, { AccordionProps } from 'components/commercetools-ui/atoms/accordion';
import Button from 'components/commercetools-ui/atoms/button';
import Input from 'components/commercetools-ui/atoms/input';
import CloseIcon from 'components/icons/close';
import useClassNames from 'helpers/hooks/useClassNames';
import { useFormat } from 'helpers/hooks/useFormat';
import { useCart } from 'frontastic';

export interface Props {
  className?: string;
  accordionProps?: AccordionProps;
}

const DiscountForm: React.FC<Props> = ({ className, accordionProps }) => {
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });

  const [code, setCode] = useState('');
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [codeIsInvalid, setCodeIsInvalid] = useState(false);
  const { redeemDiscountCode, removeDiscountCode, data } = useCart();
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    setDiscounts(data?.discountCodes ?? []);
  }, [data]);

  // const inputClassName = useClassNames([
  //   'h-40 w-full border rounded-sm px-10 py-12 text-14 placeholder:text-secondary-black disabled:bg-neutral-300 focus:outline-none',
  //   codeIsInvalid ? 'border-accent-red text-accent-red focus:border-accent-red' : 'border-neutral-300',
  // ]);

  const discountsContainerClassName = useClassNames([
    'mt-8 flex flex-wrap justify-items-start gap-12',
    discounts?.length === 0 ? 'pt-0' : 'pt-4',
  ]);

  const containerClassName = useClassNames(['py-16 text-16 border-t border-neutral-400 text-14', className]);

  const onApplyDiscount = () => {
    if (processing || !code) return;

    setProcessing(true);

    redeemDiscountCode?.(code)
      .then(() => setCode(''))
      .catch(() => {
        setCodeIsInvalid(true);
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
    setCodeIsInvalid(false);
  };

  const handleRemove = (discount: Discount) => {
    removeDiscountCode?.(discount);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onApplyDiscount();
  };

  const resetCode = () => {
    setCode('');
    setCodeIsInvalid(false);
  };

  return (
    <div className={containerClassName}>
      <AccordionBtn
        closedSectionTitle={formatCartMessage({ id: 'discount.apply', defaultMessage: 'Apply discount code' })}
        buttonClassName="text-secondary-black font-light transition-height duration-250 ease-in-out transition-top duration-250 ease-in-out"
        {...accordionProps}
        variant="plusAndMinus"
      >
        <div>
          <form className="mt-24" onSubmit={handleSubmit}>
            <div className="relative">
              <div className="relative flex w-full">
                <Input
                  variant="primary"
                  value={code}
                  placeholder={formatCartMessage({
                    id: 'cart.discount.enter',
                    defaultMessage: 'Enter your discount code',
                  })}
                  onChange={handleChange}
                  disabled={processing}
                  className="h-[48px] w-full flex-1 rounded-l-md rounded-r-none text-16"
                />
                <Button
                  className="relative mt-8 h-[48px] rounded-none  rounded-r-md px-20 py-2 font-body text-14 font-semibold"
                  type="submit"
                  size="s"
                  variant="secondary"
                >
                  {formatCartMessage({ id: 'cart.discount.apply', defaultMessage: 'Apply' })}
                </Button>
              </div>
              <p className="font-primary-black relative pt-8 text-13 font-light capitalize">
                Redeem gift voucher at payment
              </p>
              {codeIsInvalid && (
                <span
                  className="absolute right-16 top-1/2 -translate-y-1/2 normal-case"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetCode();
                  }}
                >
                  <CloseIcon className="h-10 w-10 cursor-pointer" />
                </span>
              )}
            </div>
            {codeIsInvalid && (
              <p className="mt-10 font-body text-12 font-medium normal-case leading-normal text-accent-red">
                {formatCartMessage({ id: 'codeNotValid.prefix', defaultMessage: 'Coupon code ' })} {code}
                {formatCartMessage({ id: 'codeNotValid.suffix', defaultMessage: ' is unknown.' })}
              </p>
            )}
          </form>

          {discounts && !!discounts.length && (
            <>
              {discounts.map((discount) => (
                <>
                  {discount?.state == 'NotValid' && (
                    <p className="mt-10 font-body text-12 font-medium normal-case leading-normal text-accent-red">
                      {formatCartMessage({
                        id: 'codeNotValid01',
                        defaultMessage: 'The discount code',
                      })}
                      <span className="font-bold"> {discount.code} </span>
                      {formatCartMessage({
                        id: 'codeNotValid02',
                        defaultMessage: 'has expired and will be removed from your cart.',
                      })}
                    </p>
                  )}
                  {discount?.state !== 'NotValid' && discount?.state !== 'MatchesCart' && (
                    <p className="mt-10 font-body text-12 font-medium normal-case leading-normal text-accent-red">
                      {formatCartMessage({
                        id: 'codeNotValid01',
                        defaultMessage: 'The discount code',
                      })}
                      <span className="font-bold"> {discount.code} </span>
                      {formatCartMessage({
                        id: 'codeNotApplicable02',
                        defaultMessage: 'is not applicable to the items in your cart and will be removed.',
                      })}
                    </p>
                  )}
                </>
              ))}
              <div className={discountsContainerClassName}>
                {discounts.map((discount) => (
                  <div
                    key={discount.discountId}
                    className="mr-2 flex w-fit justify-between gap-8 rounded-sm border border-neutral-400 bg-white px-8 py-4"
                  >
                    <label className="text-12 uppercase leading-[16px] text-secondary-black">{discount.code}</label>
                    <button type="button" onClick={() => handleRemove(discount)}>
                      <XMarkIcon className="h-16 w-16 text-secondary-black" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </AccordionBtn>
    </div>
  );
};

export default DiscountForm;
